/**
 * TooltipExamples.tsx
 * Comprehensive examples of HeroUI Tooltip component
 * 
 * Showcases all HeroUI Tooltip features using our shared components
 */

import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Tooltip } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { Card, CardBody, CardHeader } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Chip } from '@enfomentis/ui';
import { Input } from '@enfomentis/ui';
import { Switch } from '@enfomentis/ui';
import { 
  HelpCircle,
  Info,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Activity,
  Bell,
  Settings,
  Star,
  Target,
  BarChart3,
  PieChart,
  Calculator,
  Clock,
  Globe,
  Bookmark
} from 'lucide-react';

export const TooltipExamples: React.FC = () => {
  const [showArrow, setShowArrow] = useState(true);
  const [tooltipDelay, setTooltipDelay] = useState(0);
  const [isControlledOpen, setIsControlledOpen] = useState(false);

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Controls Section */}
        <Card className="bg-surface/50">
          <CardHeader>
            <h3 className="text-lg font-semibold text-text-primary">Tooltip Controls</h3>
          </CardHeader>
          <CardBody>
            <div className="flex flex-wrap gap-6 items-center">
              <div className="flex items-center gap-2">
                <Switch
                  isSelected={showArrow}
                  onValueChange={setShowArrow}
                  size="sm"
                >
                  Show Arrow
                </Switch>
              </div>
              
              <div className="flex items-center gap-2">
                <label className="text-sm text-text-secondary min-w-[60px]">Delay:</label>
                <Input
                  type="number"
                  value={tooltipDelay.toString()}
                  onChange={(e) => setTooltipDelay(parseInt(e.target.value) || 0)}
                  className="w-20"
                  size="sm"
                  min="0"
                  max="2000"
                  step="100"
                />
                <span className="text-xs text-text-tertiary">ms</span>
              </div>

              <div className="flex items-center gap-2">
                <Badge color="primary" variant="flat" size="sm">
                  Arrow: {showArrow ? 'On' : 'Off'}
                </Badge>
                <Badge color="secondary" variant="flat" size="sm">
                  Delay: {tooltipDelay}ms
                </Badge>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Tooltip Colors */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Tooltip Colors</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Tooltip content="Default tooltip" color="default" showArrow={showArrow} delay={tooltipDelay}>
              <Button color="default" variant="flat" startContent={<Info className="w-4 h-4" />}>
                Default
              </Button>
            </Tooltip>
            
            <Tooltip content="Primary tooltip for important information" color="primary" showArrow={showArrow} delay={tooltipDelay}>
              <Button color="primary" variant="flat" startContent={<Bell className="w-4 h-4" />}>
                Primary
              </Button>
            </Tooltip>
            
            <Tooltip content="Secondary tooltip for additional context" color="secondary" showArrow={showArrow} delay={tooltipDelay}>
              <Button color="secondary" variant="flat" startContent={<Activity className="w-4 h-4" />}>
                Secondary
              </Button>
            </Tooltip>
            
            <Tooltip content="Success tooltip for positive actions" color="success" showArrow={showArrow} delay={tooltipDelay}>
              <Button color="success" variant="flat" startContent={<CheckCircle className="w-4 h-4" />}>
                Success
              </Button>
            </Tooltip>
            
            <Tooltip content="Warning tooltip for caution messages" color="warning" showArrow={showArrow} delay={tooltipDelay}>
              <Button color="warning" variant="flat" startContent={<AlertTriangle className="w-4 h-4" />}>
                Warning
              </Button>
            </Tooltip>
            
            <Tooltip content="Danger tooltip for critical information" color="danger" showArrow={showArrow} delay={tooltipDelay}>
              <Button color="danger" variant="flat" startContent={<HelpCircle className="w-4 h-4" />}>
                Danger
              </Button>
            </Tooltip>
          </div>
        </div>

        {/* Tooltip Sizes */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Tooltip Sizes</h3>
          <div className="flex flex-wrap gap-4">
            <Tooltip content="Small tooltip" size="sm" color="primary" showArrow={showArrow} delay={tooltipDelay}>
              <Button size="sm" variant="bordered">Small Tooltip</Button>
            </Tooltip>
            
            <Tooltip content="Medium tooltip with more content space" size="md" color="primary" showArrow={showArrow} delay={tooltipDelay}>
              <Button size="md" variant="bordered">Medium Tooltip</Button>
            </Tooltip>
            
            <Tooltip content="Large tooltip with plenty of space for detailed information" size="lg" color="primary" showArrow={showArrow} delay={tooltipDelay}>
              <Button size="lg" variant="bordered">Large Tooltip</Button>
            </Tooltip>
          </div>
        </div>

        {/* Tooltip Placements */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Tooltip Placements</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 p-8">
            
            {/* Top row */}
            <div className="flex justify-center">
              <Tooltip content="Top Start" placement="top-start" color="primary" showArrow={showArrow} delay={tooltipDelay}>
                <Button size="sm" variant="flat">Top Start</Button>
              </Tooltip>
            </div>
            
            <div className="flex justify-center">
              <Tooltip content="Top Center" placement="top" color="primary" showArrow={showArrow} delay={tooltipDelay}>
                <Button size="sm" variant="flat">Top</Button>
              </Tooltip>
            </div>
            
            <div className="flex justify-center">
              <Tooltip content="Top End" placement="top-end" color="primary" showArrow={showArrow} delay={tooltipDelay}>
                <Button size="sm" variant="flat">Top End</Button>
              </Tooltip>
            </div>
            
            <div></div> {/* Empty space */}
            
            {/* Left column */}
            <Tooltip content="Left Start" placement="left-start" color="secondary" showArrow={showArrow} delay={tooltipDelay}>
              <Button size="sm" variant="flat">Left Start</Button>
            </Tooltip>
            
            <div></div> {/* Center space */}
            <div></div> {/* Center space */}
            
            {/* Right column */}
            <Tooltip content="Right Start" placement="right-start" color="secondary" showArrow={showArrow} delay={tooltipDelay}>
              <Button size="sm" variant="flat">Right Start</Button>
            </Tooltip>
            
            <Tooltip content="Left Center" placement="left" color="secondary" showArrow={showArrow} delay={tooltipDelay}>
              <Button size="sm" variant="flat">Left</Button>
            </Tooltip>
            
            <div></div> {/* Center space */}
            <div></div> {/* Center space */}
            
            <Tooltip content="Right Center" placement="right" color="secondary" showArrow={showArrow} delay={tooltipDelay}>
              <Button size="sm" variant="flat">Right</Button>
            </Tooltip>
            
            <Tooltip content="Left End" placement="left-end" color="secondary" showArrow={showArrow} delay={tooltipDelay}>
              <Button size="sm" variant="flat">Left End</Button>
            </Tooltip>
            
            <div></div> {/* Center space */}
            <div></div> {/* Center space */}
            
            <Tooltip content="Right End" placement="right-end" color="secondary" showArrow={showArrow} delay={tooltipDelay}>
              <Button size="sm" variant="flat">Right End</Button>
            </Tooltip>
            
            <div></div> {/* Empty space */}
            
            {/* Bottom row */}
            <div className="flex justify-center">
              <Tooltip content="Bottom Start" placement="bottom-start" color="success" showArrow={showArrow} delay={tooltipDelay}>
                <Button size="sm" variant="flat">Bottom Start</Button>
              </Tooltip>
            </div>
            
            <div className="flex justify-center">
              <Tooltip content="Bottom Center" placement="bottom" color="success" showArrow={showArrow} delay={tooltipDelay}>
                <Button size="sm" variant="flat">Bottom</Button>
              </Tooltip>
            </div>
            
            <div className="flex justify-center">
              <Tooltip content="Bottom End" placement="bottom-end" color="success" showArrow={showArrow} delay={tooltipDelay}>
                <Button size="sm" variant="flat">Bottom End</Button>
              </Tooltip>
            </div>
          </div>
        </div>

        {/* Advanced Features */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Advanced Features</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            
            {/* Controlled tooltip */}
            <Card>
              <CardHeader>
                <h4 className="text-md font-medium">Controlled State</h4>
              </CardHeader>
              <CardBody className="space-y-3">
                <Tooltip
                  content="This is a controlled tooltip"
                  isOpen={isControlledOpen}
                  color="primary"
                  showArrow={showArrow}
                >
                  <Button variant="bordered" className="w-full">Controlled Target</Button>
                </Tooltip>
                <Button
                  size="sm"
                  variant="flat"
                  color="primary"
                  onPress={() => setIsControlledOpen(!isControlledOpen)}
                  className="w-full"
                >
                  {isControlledOpen ? 'Hide' : 'Show'} Tooltip
                </Button>
              </CardBody>
            </Card>

            {/* Rich content tooltip */}
            <Card>
              <CardHeader>
                <h4 className="text-md font-medium">Rich Content</h4>
              </CardHeader>
              <CardBody>
                <Tooltip
                  content={
                    <div className="px-1 py-2">
                      <div className="text-small font-bold">Trading Metrics</div>
                      <div className="text-tiny">Win Rate: 78%</div>
                      <div className="text-tiny">Profit Factor: 2.3</div>
                      <div className="text-tiny">Max Drawdown: 5.2%</div>
                    </div>
                  }
                  color="secondary"
                  size="lg"
                  showArrow={showArrow}
                  delay={tooltipDelay}
                >
                  <Button variant="bordered" startContent={<BarChart3 className="w-4 h-4" />} className="w-full">
                    Trading Stats
                  </Button>
                </Tooltip>
              </CardBody>
            </Card>

            {/* Disabled tooltip */}
            <Card>
              <CardHeader>
                <h4 className="text-md font-medium">Disabled State</h4>
              </CardHeader>
              <CardBody>
                <Tooltip content="This tooltip is disabled" isDisabled color="warning" showArrow={showArrow}>
                  <Button variant="bordered" className="w-full">No Tooltip</Button>
                </Tooltip>
                <p className="text-xs text-text-tertiary mt-2">Hover won't show tooltip</p>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Trading Journal Context */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Trading Journal Tooltips</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            
            <Tooltip content="Current profit/loss for this position" color="success" showArrow={showArrow}>
              <Button color="success" variant="flat" startContent={<TrendingUp className="w-4 h-4" />}>
                P&L
              </Button>
            </Tooltip>
            
            <Tooltip content="Total portfolio value including cash and positions" color="primary" showArrow={showArrow}>
              <Button color="primary" variant="flat" startContent={<DollarSign className="w-4 h-4" />}>
                Portfolio
              </Button>
            </Tooltip>
            
            <Tooltip content="Risk percentage of portfolio on this trade" color="warning" showArrow={showArrow}>
              <Button color="warning" variant="flat" startContent={<Target className="w-4 h-4" />}>
                Risk %
              </Button>
            </Tooltip>
            
            <Tooltip content="Win rate over the last 30 trades" color="secondary" showArrow={showArrow}>
              <Button color="secondary" variant="flat" startContent={<Activity className="w-4 h-4" />}>
                Win Rate
              </Button>
            </Tooltip>
            
            <Tooltip content="Maximum consecutive losing trades" color="danger" showArrow={showArrow}>
              <Button color="danger" variant="flat" startContent={<TrendingDown className="w-4 h-4" />}>
                Drawdown
              </Button>
            </Tooltip>
            
            <Tooltip content="Average time held per winning trade" color="default" showArrow={showArrow}>
              <Button color="default" variant="flat" startContent={<Clock className="w-4 h-4" />}>
                Hold Time
              </Button>
            </Tooltip>
            
            <Tooltip content="Profit factor (gross profit / gross loss)" color="success" showArrow={showArrow}>
              <Button color="success" variant="flat" startContent={<Calculator className="w-4 h-4" />}>
                Profit Factor
              </Button>
            </Tooltip>
            
            <Tooltip content="Portfolio diversification across sectors" color="primary" showArrow={showArrow}>
              <Button color="primary" variant="flat" startContent={<PieChart className="w-4 h-4" />}>
                Sectors
              </Button>
            </Tooltip>
          </div>
        </div>

        {/* Interactive Elements */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Interactive Elements with Tooltips</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            <Card>
              <CardHeader>
                <h4 className="text-md font-medium">Form Fields</h4>
              </CardHeader>
              <CardBody className="space-y-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-text-primary">Stock Symbol</label>
                    <Tooltip content="Enter the symbol for the stock you want to trade" placement="right" color="primary">
                      <HelpCircle className="w-4 h-4 text-text-secondary cursor-help" />
                    </Tooltip>
                  </div>
                  <Input
                    placeholder="e.g. AAPL"
                    startContent={<Activity className="w-4 h-4 text-text-tertiary" />}
                  />
                </div>
                
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-text-primary">Quantity</label>
                    <Tooltip content="Number of shares to purchase (max 1000)" placement="right" color="secondary">
                      <HelpCircle className="w-4 h-4 text-text-secondary cursor-help" />
                    </Tooltip>
                  </div>
                  <Input
                    placeholder="100"
                    type="number"
                    startContent={<Calculator className="w-4 h-4 text-text-tertiary" />}
                  />
                </div>
                
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-text-primary">Limit Price</label>
                    <Tooltip content="Target price for your limit order" placement="right" color="warning">
                      <HelpCircle className="w-4 h-4 text-text-secondary cursor-help" />
                    </Tooltip>
                  </div>
                  <Input
                    placeholder="$150.00"
                    startContent={<DollarSign className="w-4 h-4 text-text-tertiary" />}
                  />
                </div>
                
                <div className="mt-6">
                  <p className="text-xs text-text-tertiary">
                    💡 Tip: Hover over the help icons next to field labels for contextual information
                  </p>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h4 className="text-md font-medium">Action Buttons</h4>
              </CardHeader>
              <CardBody className="space-y-3">
                <Tooltip content="Add this trade to your watchlist" color="primary">
                  <Button color="primary" variant="flat" startContent={<Bookmark className="w-4 h-4" />} className="w-full">
                    Add to Watchlist
                  </Button>
                </Tooltip>
                
                <Tooltip content="Mark this strategy as favorite" color="warning">
                  <Button color="warning" variant="flat" startContent={<Star className="w-4 h-4" />} className="w-full">
                    Favorite Strategy
                  </Button>
                </Tooltip>
                
                <Tooltip content="Share this analysis with your team" color="secondary">
                  <Button color="secondary" variant="flat" startContent={<Globe className="w-4 h-4" />} className="w-full">
                    Share Analysis
                  </Button>
                </Tooltip>
                
                <Tooltip content="Set up automatic notifications for this stock" color="success">
                  <Button color="success" variant="flat" startContent={<Bell className="w-4 h-4" />} className="w-full">
                    Enable Alerts
                  </Button>
                </Tooltip>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h4 className="text-md font-medium">Wrapper Tooltips</h4>
              </CardHeader>
              <CardBody className="space-y-3">
                <Tooltip content="Click to configure advanced trading parameters" color="primary">
                  <div>
                    <Button variant="bordered" startContent={<Settings className="w-4 h-4" />} className="w-full">
                      Advanced Settings
                    </Button>
                  </div>
                </Tooltip>
                
                <Tooltip content="Switch to enable automatic position sizing based on risk percentage" color="success">
                  <div className="flex items-center justify-between p-2 rounded-md border border-border">
                    <span className="text-sm">Auto Position Size</span>
                    <Switch size="sm" />
                  </div>
                </Tooltip>
                
                <Tooltip content="Input field is disabled while market is closed" color="warning">
                  <div>
                    <Input
                      placeholder="Market order"
                      isDisabled
                      className="w-full"
                    />
                  </div>
                </Tooltip>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Usage Guide */}
        <Card className="bg-surface/30">
          <CardHeader>
            <h3 className="text-lg font-semibold text-text-primary">Usage Guide</h3>
          </CardHeader>
          <CardBody>
            <div className="space-y-4">
              <div>
                <Chip color="primary" variant="flat" size="sm" className="mb-2">Basic Usage</Chip>
                <pre className="text-xs bg-content1 p-3 rounded-md overflow-x-auto">
{`import { Tooltip } from '@enfomentis/ui';

<Tooltip content="Helpful information">
  <Button>Hover me</Button>
</Tooltip>`}
                </pre>
              </div>
              
              <div>
                <Chip color="secondary" variant="flat" size="sm" className="mb-2">Advanced Features</Chip>
                <ul className="text-sm text-text-secondary space-y-1 list-disc list-inside">
                  <li><strong>Colors:</strong> default, primary, secondary, success, warning, danger</li>
                  <li><strong>Sizes:</strong> sm, md, lg for different content amounts</li>
                  <li><strong>Placements:</strong> 12 positioning options (top, bottom, left, right + variants)</li>
                  <li><strong>Arrow Support:</strong> Optional arrow pointing to trigger element</li>
                  <li><strong>Delay Control:</strong> Configurable open/close delays</li>
                  <li><strong>Rich Content:</strong> Support for JSX content and complex layouts</li>
                  <li><strong>Controlled State:</strong> Programmatic control of tooltip visibility</li>
                  <li><strong>Form Field Pattern:</strong> Use help icons next to labels instead of wrapping inputs</li>
                  <li><strong>Wrapper Elements:</strong> Wrap complex components with div for better tooltip behavior</li>
                  <li><strong>Accessibility:</strong> Full keyboard navigation and ARIA support</li>
                </ul>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </PlaygroundPageLayout>
  );
};